/**
 * La curva de la reescritura: cómo cambió el libro, versión a versión.
 *
 * Es el gráfico que no puede dibujar nadie más, y no por listeza técnica: para tenerlo hay que
 * haber guardado la medición de cada versión DESDE LA PRIMERA, y eso no se reconstruye después.
 * Quien empieza a medir en la versión treinta tiene un punto, no una curva.
 *
 * Aquí no se calcula nada. Se lee lo que el motor ya midió y se pinta. Si aparece la tentación de
 * inventar un dato para tapar un hueco de la línea, es señal de haberse pasado al lado equivocado
 * del producto: un hueco es información —esa versión no está medida— y se dice.
 *
 * EL EJE NO ARRANCA EN CERO, y eso es deliberado. Con base cero, una reescritura que mueve el 5 %
 * del manuscrito sale plana: la variación queda aplastada contra el techo. Se trunca y se rotulan
 * los extremos, para que el truncado se VEA en lugar de engañar.
 */
import { useCallback, useEffect, useState } from "react";
import { pasadas, medirPasadas, miles, versionesDelLibro, preverVolver, volverAVersion } from "../api";
import type { Trabajo, Serie, Version, VersionDelLibro, PrevisionVolver } from "../api";
import { frasesDeVolver } from "../logica";

const W = 720, H = 250, L = 56, R = 46, T = 16, B = 30;

export default function Pasadas({ carpeta, doc = null, pulso = 0, trabajo, alLanzar, alCambiarDoc, alCambio }: {
  carpeta: string; doc?: string | null; pulso?: number; trabajo?: Trabajo | null; alLanzar?: () => void;
  /** Al volver a una versión, el manuscrito pasa a ser la copia nueva. */
  alCambiarDoc?: (ruta: string) => void; alCambio?: () => void;
}) {
  const [s, setS] = useState<Serie | null>(null);
  const [midiendo, setMidiendo] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);

  const cargar = useCallback(() => {
    if (!carpeta) return;
    pasadas(carpeta, doc).then(setS).catch(() => setFallo("No he podido leer las mediciones."));
  }, [carpeta, doc]);

  useEffect(cargar, [cargar, pulso]);

  // Medir es un trabajo del motor (R-17): se sabe cuándo termina y la curva se relee sola (pulso).
  // Con un motor anterior sin trabajos se vuelve a preguntar a los veinte segundos, como antes.
  async function medir() {
    setMidiendo(true);
    try {
      const r = await medirPasadas(carpeta, doc);
      setS(r);
      if ((r as { trabajo?: Trabajo }).trabajo) { alLanzar?.(); setMidiendo(false); return; }
      setTimeout(() => { cargar(); setMidiendo(false); }, 20000);
    } catch {
      setMidiendo(false);
      setFallo("No se ha podido lanzar la medición.");
    }
  }
  const midiendoAhora = midiendo || trabajo?.estado === "en_curso";

  if (!carpeta) {
    return (
      <section className="carta">
        <h2>Pasadas<span className="sub">sin manuscrito</span></h2>
        <p className="vacio">Elige antes la carpeta de tu manuscrito.</p>
      </section>
    );
  }

  const vs = s?.versiones ?? [];

  // 17-sep: una carpeta con varios libros mezclaba sus versiones. Ahora la curva es del libro abierto,
  // y se dice qué más hay: la carpeta de un libro debería ser solo suya.
  const otros = s?.otrosLibros ?? [];
  const avisoOtros = otros.length > 0 && (
    <p className="aviso">
      En esta carpeta hay versiones de {otros.length === 1 ? "otro libro" : `${otros.length} libros más`} ({otros.slice(0, 4).join(", ")}{otros.length > 4 ? "…" : ""}).
      La curva solo cuenta {s?.libro ? <b>{s.libro}</b> : "el libro abierto"}. Lo más limpio es darle a cada libro su propia carpeta.
    </p>
  );

  const volverAtras = doc && <VolverAtras carpeta={carpeta} doc={doc} pulso={pulso} alCambiarDoc={alCambiarDoc} alCambio={alCambio} />;

  if (!vs.length) {
    return (
      <>
      <section className="carta">
        <h2>Pasadas<span className="sub">sin medir</span></h2>
        {avisoOtros}
        <p className="vacio">
          Aquí va la curva de tu reescritura: cuánto creció o encogió el libro en cada versión, y
          qué parte de lo que se te propuso te quedaste. Se construye midiendo los builds que ya
          tienes en la carpeta.
        </p>
        {/* Rais, 21-sep: «le doy a medir y no hace nada». Con UNA versión no hay cadena: la curva
            compara cada versión con la anterior y el motor contestaba «bien» sin medir nada. */}
        {s && s.sinMedir === 1 && (
          <p className="aviso">
            De este libro solo tienes <b>una versión</b>, y la curva compara cada versión con la anterior: no hay nada que medir
            todavía. Empieza sola cuando hagas la primera pasada, que crea la versión 2.
          </p>
        )}
        {s && s.sinMedir > 1 && (
          <>
            <p className="vacio"><b>{s.sinMedir}</b> versiones sin medir{s.libro ? " de este libro" : " en esta carpeta"}.</p>
            <div className="dir">
              <button className="btn" onClick={medir} disabled={midiendoAhora}>
                {midiendoAhora ? "Midiendo…" : "Medir la cadena"}
              </button>
            </div>
          </>
        )}
        {trabajo?.estado === "fallido" && !midiendoAhora && <p className="vacio" style={{ color: "var(--acc)" }}>La última medición no se pudo hacer: {trabajo.error}</p>}
        {fallo && <p className="vacio">{fallo}</p>}
      </section>
      {volverAtras}
      </>
    );
  }

  // ── La curva ───────────────────────────────────────────────────────────────────────────────
  const palabras = vs.map((v) => v.palabras);
  const brutoMax = Math.max(...palabras), brutoMin = Math.min(...palabras);
  const margen = Math.max(1, (brutoMax - brutoMin) * 0.12);
  const maxP = brutoMax + margen, minP = Math.max(0, brutoMin - margen);
  const rango = maxP - minP || 1;
  const pw = W - L - R, ph = H - T - B;
  const x = (i: number) => L + (vs.length === 1 ? pw / 2 : (i * pw) / (vs.length - 1));
  const yP = (p: number) => T + ph - ((p - minP) / rango) * ph;

  const linea = vs.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${yP(v.palabras).toFixed(1)}`).join(" ");
  const area = `${linea} L${x(vs.length - 1).toFixed(1)},${T + ph} L${x(0).toFixed(1)},${T + ph} Z`;

  const conNota = vs.map((v, i) => ({ v, i })).filter((o) => typeof o.v.nota === "number");
  const yN = (n: number) => T + ph - (n / 100) * ph;
  const lineaNota = conNota.map((o, k) =>
    `${k ? "L" : "M"}${x(o.i).toFixed(1)},${yN(o.v.nota as number).toFixed(1)}`).join(" ");

  const decididas = vs.filter((v) => v.propuestas);
  const total = (f: (v: Version) => number | null) =>
    decididas.reduce((a, v) => a + (f(v) ?? 0), 0);
  const aceptadas = total((v) => v.aceptadas), propuestas = total((v) => v.propuestas);

  const primera = vs[0], ultima = vs[vs.length - 1];
  const neto = ultima.palabras - primera.palabras;

  return (
    <>
      {avisoOtros}
      <div className="rejilla">
        <section className="carta">
          <h2>Versiones medidas<span className="sub">{s?.libro ? "de este libro" : "de esta carpeta"}</span></h2>
          <div className="cifra"><b>{vs.length}</b><span>de la v{primera.n} a la v{ultima.n}</span></div>
          {s && s.sinMedir > 0 && <p className="vacio">{s.sinMedir} sin medir todavía.</p>}
        </section>
        <section className="carta">
          <h2>El neto<span className="sub">primera → última</span></h2>
          <div className="cifra">
            <b>{neto > 0 ? "+" : ""}{miles(neto)}</b><span>palabras</span>
          </div>
          <p className="vacio">{miles(primera.palabras)} → {miles(ultima.palabras)}</p>
        </section>
        <section className="carta">
          <h2>Lo que te quedaste<span className="sub">de lo evaluable</span></h2>
          <div className="cifra">
            <b>{propuestas ? Math.round((aceptadas / propuestas) * 100) + " %" : "—"}</b>
            <span>{propuestas ? `de ${miles(propuestas)} marcas` : "sin decisiones medidas"}</span>
          </div>
          {!propuestas && (
            <p className="vacio">
              Se mide comparando dos builds seguidos, y solo cuentan las marcas de más de 25
              caracteres: las de una palabra no se pueden rastrear entre versiones.
            </p>
          )}
        </section>
      </div>

      <section className="carta">
        <h2>La curva de tu reescritura
          <span className="sub">palabras por versión{conNota.length ? " · y la nota del informe de lectura" : ""}</span>
        </h2>

        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img"
             aria-label={`Palabras por versión, de ${miles(primera.palabras)} a ${miles(ultima.palabras)}`}>
          {/* Malla mínima: cuatro líneas para leer altura, nada más. */}
          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <line key={f} className="malla" x1={L} x2={W - R}
                  y1={T + ph * f} y2={T + ph * f} />
          ))}
          {/* El área bajo la curva, en degradado que se desvanece: el mismo recurso de la maqueta y de
              la landing. En oro, porque la curva mide el trabajo de la máquina sobre el libro. */}
          <defs>
            <linearGradient id="oroArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFC400" stopOpacity="0.26" />
              <stop offset="100%" stopColor="#FFC400" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill="url(#oroArea)" />
          <path d={linea} fill="none" stroke="var(--acc)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {vs.map((v, i) => (
            <circle key={v.n} cx={x(i)} cy={yP(v.palabras)} r="3" fill="var(--acc)">
              <title>{`v${v.n} · ${miles(v.palabras)} palabras · ${v.delta && v.delta > 0 ? "+" : ""}${miles(v.delta ?? 0)}`}</title>
            </circle>
          ))}
          {conNota.length > 1 && (
            <path d={lineaNota} fill="none" stroke="var(--acc)" strokeWidth="2" strokeDasharray="4 3" />
          )}

          {/* Los extremos, rotulados: es lo que hace honesto un eje truncado. */}
          <text className="eje" x={L - 8} y={T + 10} textAnchor="end">{miles(Math.round(maxP))}</text>
          <text className="eje" x={L - 8} y={T + ph} textAnchor="end">{miles(Math.round(minP))}</text>
          <text className="eje" x={L} y={H - 8}>v{primera.n}</text>
          <text className="eje" x={W - R} y={H - 8} textAnchor="end">v{ultima.n}</text>
        </svg>

        <p className="vacio">
          El eje no arranca en cero: con base cero, una reescritura que mueve el 5 % del manuscrito
          saldría plana. Va de {miles(Math.round(minP))} a {miles(Math.round(maxP))} palabras.
        </p>

        <div className="dir">
          <button className="btn fantasma" onClick={medir} disabled={midiendoAhora}>
            {midiendoAhora ? "Midiendo…" : "Volver a medir la cadena"}
          </button>
        </div>
      </section>

      <section className="carta">
        <h2>Versión a versión<span className="sub">{vs.length} builds</span></h2>
        <div className="mapa">
          {[...vs].reverse().map((v) => (
            <div className="cap" key={v.n}>
              <span className="idx mono">v{v.n}</span>
              <span className="tit">{v.build ?? ""}</span>
              <span className="raya">
                <i style={{ width: `${((v.palabras - minP) / rango) * 100}%` }} />
              </span>
              <b className="mono">{miles(v.palabras)}</b>
              <span className="meta">
                {v.delta ? `${v.delta > 0 ? "+" : ""}${miles(v.delta)}` : "="}
                {v.marcas ? ` · ${miles(v.marcas)} marcas` : ""}
                {v.propuestas ? ` · ${v.aceptadas}/${v.propuestas} aceptadas` : ""}
              </span>
            </div>
          ))}
        </div>
      </section>

      {volverAtras}
    </>
  );
}

/**
 * VOLVER A UNA VERSIÓN ANTERIOR (P-04, 29-sep-2026). Rais: «hay que ir a una versión anterior». Se
 * elige la versión, se lee qué va a pasar —la copia nueva, lo que se queda, lo que se reabre— y se
 * confirma. Nada se borra: la versión elegida se copia como la siguiente.
 */
function VolverAtras({ carpeta, doc, pulso, alCambiarDoc, alCambio }: {
  carpeta: string; doc: string; pulso: number; alCambiarDoc?: (ruta: string) => void; alCambio?: () => void;
}) {
  const [vs, setVs] = useState<VersionDelLibro[] | null>(null);
  const [elegida, setElegida] = useState("");
  const [prev, setPrev] = useState<PrevisionVolver | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    versionesDelLibro(carpeta, doc).then((l) => { if (vivo) { setVs(l); setElegida(""); setPrev(null); } });
    return () => { vivo = false; };
  }, [carpeta, doc, pulso]);

  // Un motor anterior a la 1.6.32 no sabe volver atrás; con una sola versión no hay adónde.
  if (!vs || vs.length < 2) return null;
  const anteriores = vs.slice(1);

  async function prever(ruta: string) {
    setElegida(ruta); setPrev(null); setFallo(null); setHecho(null);
    if (!ruta) return;
    setOcupado(true);
    try { setPrev(await preverVolver(carpeta, doc, ruta)); }
    catch (e) { setFallo((e as Error).message); }
    finally { setOcupado(false); }
  }
  async function volver() {
    if (!prev) return;
    setOcupado(true); setFallo(null);
    try {
      const r = await volverAVersion(carpeta, doc, elegida);
      setHecho(`Hecho: la v${r.vNueva} es ahora tu manuscrito, igual que la v${r.vA}. Las demás versiones siguen en la carpeta.`);
      setPrev(null); setElegida("");
      alCambiarDoc?.(r.nueva); alCambio?.();
    } catch (e) { setFallo((e as Error).message); }
    finally { setOcupado(false); }
  }

  return (
    <section className="carta">
      <h2>Volver a una versión anterior<span className="sub">sin borrar nada</span></h2>
      <p className="vacio">Si una pasada no te convence, vuelve a una versión de antes. Se copia como una versión nueva: las que hay ahora se quedan en la carpeta, por si cambias de idea.</p>
      <div className="dir">
        <select className="escribir" value={elegida} disabled={ocupado} onChange={(e) => prever(e.target.value)} aria-label="Versión a la que volver">
          <option value="">Elige la versión…</option>
          {anteriores.map((v) => (
            <option key={v.ruta} value={v.ruta}>v{v.v} · {v.nombre}{v.fecha ? ` · ${new Date(v.fecha).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}` : ""}</option>
          ))}
        </select>
      </div>
      {ocupado && !prev && elegida && <p className="vacio">Mirando qué cambiaría…</p>}
      {prev && (
        <>
          <ul className="notas">{frasesDeVolver(prev).map((f, i) => <li key={i}>{f}</li>)}</ul>
          <div className="botones">
            <button className="btn" disabled={ocupado} onClick={volver}>{ocupado ? "Volviendo…" : `Volver a la v${prev.vA}`}</button>
            <button className="btn texto" disabled={ocupado} onClick={() => prever("")}>Ahora no</button>
          </div>
        </>
      )}
      {hecho && <p className="vacio" style={{ color: "var(--ok)" }}>{hecho}</p>}
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
    </section>
  );
}
